import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Table } from "react-bootstrap";
import { Link, Navigate } from "react-router-dom";
import { Api } from "../api.config";
import { Spinner } from "../common/Spinner";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { LinkGen, QueryKeys } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { formatCanvaAuditEvent } from "./canvaAuditFormat";
import { useCanvaQuery } from "./useIntegrations";

const PAGE_SIZE = 25;

export function PageCanvaAudit() {
	const isCanvaEnabled = useFeatureFlag("CanvaIntegration");
	const { data: canvaStatus } = useCanvaQuery();
	const { id: companyId } = useCompany();
	const [limit, setLimit] = useState(PAGE_SIZE);

	const { data, isLoading, isFetching } = useQuery({
		queryKey: QueryKeys.Integrations.Canva.AuditEvents(companyId, limit, 0),
		queryFn: async () => {
			const res = await Api.CanvaAuditEvents(limit, 0);
			return res.data;
		},
		enabled: isCanvaEnabled && canvaStatus?.connected === true,
	});

	if (!isCanvaEnabled) {
		return <Navigate to={LinkGen.Settings.Apps()} replace />;
	}

	if (canvaStatus && !canvaStatus.connected) {
		return <Navigate to={LinkGen.Settings.Apps()} replace />;
	}

	const hasMore = (data?.total ?? 0) > (data?.items.length ?? 0);

	return (
		<AppPage
			title="Canva activity"
			subtitle="Import, sync, and connection events for your workspace."
			className="settings canva-audit-page"
		>
			<Link to={LinkGen.Settings.Apps()} className="canva-audit-page__back">
				← Back to integrations
			</Link>

			{isLoading ? (
				<div className="canva-audit-page__loading">
					<Spinner />
				</div>
			) : !data || data.items.length === 0 ? (
				<p className="text-muted mb-0">No Canva activity recorded yet.</p>
			) : (
				<>
					<Table responsive className="canva-audit-page__table">
						<thead>
							<tr>
								<th>When</th>
								<th>Event</th>
								<th>User</th>
							</tr>
						</thead>
						<tbody>
							{data.items.map((event) => (
								<tr key={event.id}>
									<td className="text-nowrap">
										{new Date(event.createdAt).toLocaleString()}
									</td>
									<td>{formatCanvaAuditEvent(event)}</td>
									<td className="text-muted">{event.userEmail ?? "—"}</td>
								</tr>
							))}
						</tbody>
					</Table>
					{hasMore && (
						<div className="canva-audit-page__more">
							<Button
								disabled={isFetching}
								onClick={() => setLimit((current) => current + PAGE_SIZE)}
							>
								{isFetching ? "Loading…" : "Load more"}
							</Button>
							<span className="text-muted small">
								Showing {data.items.length} of {data.total}
							</span>
						</div>
					)}
				</>
			)}
		</AppPage>
	);
}
